import { type Paginated } from '@deque/pagination-utils';
import { apiRequest } from '../../api-client';

export const SECURITY_EVENT_LOG_CSV_FILENAME = 'security-event-log.csv';

export interface SecurityEvent {
  event_id: string;
  event_type: string;
  category: string;
  timestamp: string;
  actor_user_id: string | null;
  actor_user_name: string | null;
  actor_email: string | null;
  actor_display_name: string | null;
  actor_ip_address: string | null;
  enterprise_id: string | null;
  enterprise_name: string | null;
  admin_event: boolean;
  data: Record<string, unknown> | null;
}

export interface SecurityEventLogParams {
  start_date?: string;
  end_date?: string;
  user_id?: string;
  event_type?: string;
  category?: string;
  admin_only?: boolean;
  page?: number;
  per_page?: number;
}

interface GetSecurityEventLogParams extends SecurityEventLogParams {
  token: string;
  enterpriseId: string | null;
}

interface ExportSecurityEventLogParams {
  token: string;
  enterpriseId: string | null;
  start_date?: string;
  end_date?: string;
  user_id?: string;
  event_type?: string;
  category?: string;
  admin_only?: boolean;
}

function buildQueryString(
  params: Record<string, string | number | boolean | undefined>
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== ''
  );
  if (entries.length === 0) {
    return '';
  }
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
}

export function getSecurityEventLog({
  token,
  enterpriseId,
  page,
  per_page,
  ...filters
}: GetSecurityEventLogParams): Promise<Paginated<SecurityEvent[]>> {
  const qs = buildQueryString(filters);
  const endpoint = enterpriseId
    ? `/api-internal/enterprises/${enterpriseId}/security-event-log${qs}`
    : `/api-internal/security-event-log${qs}`;

  return apiRequest<SecurityEvent[]>({
    method: 'GET',
    endpoint,
    token,
    page,
    per_page
  }) as Promise<Paginated<SecurityEvent[]>>;
}

export async function exportSecurityEventLog({
  token,
  enterpriseId,
  ...filters
}: ExportSecurityEventLogParams): Promise<Blob> {
  const qs = buildQueryString(filters);
  const endpoint = enterpriseId
    ? `/api-internal/enterprises/${enterpriseId}/security-event-log/export${qs}`
    : `/api-internal/security-event-log/export${qs}`;

  const response = await apiRequest<Response>({
    method: 'GET',
    raw: true,
    headers: {
      Accept: 'text/csv'
    },
    endpoint,
    token
  });
  return response.blob();
}
